'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { SheetPage, useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { ContactPatch, Person, SearchHit } from '@/lib/types';
import { emailOk, formatPhone, memberSubline, phoneOk } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { PhotoAvatar } from './PhotoAvatar';
import { Form, GroupedSelectField, SelectField, TextField } from './forms';
import { MedicalPage } from './MedicalPage';
import { EmergencyPage } from './EmergencyPage';
import { RelationshipPage, relationshipLabel } from './RelationshipPage';
import {
  AskPage,
  ChangeRelationshipPage,
  FamilyPage,
  FindPage,
  NotePage,
  PickPage,
  SearchPage,
  getRelateDraft,
} from './FamilyPages';
import { applyRelation, replaceRelationId } from './relations';
import { PersonSheetHeader } from '@church/nextjs-ui/website/profile';
import { findSpouse, isMarried, marriedToRelationship } from './spouse';
import { CARD, Heading, PageBody, ProfileSheet } from './ui';

export type MemberPage =
  | 'about'
  | 'contact'
  | 'family'
  | 'relate'
  | 'medical'
  | 'emergency'
  | 'relationship';

/**
 * The editor for one person in the household (the signed-in person
 * included). Every card on the page opens straight to its page here; the
 * sheet carries the whole form, the card only shows the result.
 */
export function MemberSheet({
  contactId,
  defaultPage = 'about',
  onClose,
}: {
  contactId: number | null;
  defaultPage?: MemberPage;
  onClose: () => void;
}) {
  const { data } = useProfile();
  const person =
    contactId === null
      ? null
      : contactId === data.me.contactId
        ? data.me
        : (data.members.find((m) => m.contactId === contactId) ?? null);
  const isMe = person?.contactId === data.me.contactId;

  // The requested page IS the sheet's main page: opening straight to
  // "about" or "medical" must not grow a Back bar to a page that never
  // existed. The spouse flow stacks on top of it. Only "emergency" and
  // "relate" are drilled pages (their card's page sits underneath).
  return (
    <ProfileSheet
      open={person !== null}
      onClose={onClose}
      // "emergency" and "relate" are pages of the Medical and Family sheets:
      // opening on one seeds the history main → page, so Back reaches the list.
      defaultPage={defaultPage === 'emergency' || defaultPage === 'relate' ? defaultPage : 'main'}
      header={
        person ? (
          <MemberHeader person={person} isMe={isMe} page={defaultPage} />
        ) : (
          <div className="h-16" />
        )
      }
    >
      <SheetPage name="main">
        {/* One sheet per card, and its main page is that card and nothing else:
            About is the form (its Contact section included), Family is the
            family list. A card element that wants to land "closer" opens the
            same page scrolled to its section ("contact"), or a drilled page with
            this one underneath. The Subscriptions card has no sheet: its
            switches write from the card. */}
        {person && (defaultPage === 'about' || defaultPage === 'contact') && (
          <AboutForm person={person} onDone={onClose} focusContact={defaultPage === 'contact'} />
        )}
        {person && (defaultPage === 'family' || defaultPage === 'relate') && (
          <FamilyPage person={person} />
        )}
        {person && (defaultPage === 'medical' || defaultPage === 'emergency') && (
          <div className="pii">
            <MedicalPage person={person} />
          </div>
        )}
        {person && defaultPage === 'relationship' && !isMe && (
          <RelationshipPage subject={data.me} other={person} onDone={onClose} />
        )}
      </SheetPage>
      <SheetPage name="emergency" title="Emergency contact">
        {person && <EmergencyBack person={person} />}
      </SheetPage>
      <SheetPage
        name="relate"
        title={person ? `${person.nickname || person.firstName}'s family` : ''}
      >
        {person && <RelatePage subject={person} onDone={onClose} />}
      </SheetPage>
      <SheetPage name="rechange" title="How are you related?">
        {person && <ChangeRelationshipPage />}
      </SheetPage>
      <SheetPage name="find" title="Find them">
        {person && isMe && <FindPage />}
      </SheetPage>
      <SheetPage name="pick" title="How are you related?">
        {person && isMe && <PickPage />}
      </SheetPage>
      <SheetPage name="ask" title="Send a request">
        {person && isMe && <AskPage onDone={onClose} />}
      </SheetPage>
      <SheetPage name="spouse" title="Who are you married to?">
        {person && isMe && <SpousePage onDone={onClose} />}
      </SheetPage>
      <SheetPage name="search" title="Find them">
        {person && isMe && <SpouseSearch />}
      </SheetPage>
      <SheetPage name="note" title="Send a request">
        {person && isMe && <SpouseNote onDone={onClose} />}
      </SheetPage>
    </ProfileSheet>
  );
}

/**
 * Reads the "who" draft when the page renders, not when the sheet is built:
 * the Family page sets the draft and navigates here, and the sheet itself
 * does not re-render on that, so a read inside the sheet's JSX saw null and
 * the page came up blank.
 */
/** Scrolls its child into view on open, for a card element that pointed straight at it. */
function Focus({ when, children }: { when: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!when) return;
    const id = window.setTimeout(
      () => ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
      60
    );
    return () => window.clearTimeout(id);
  }, [when]);
  return <div ref={ref}>{children}</div>;
}

/** The emergency editor as a page of the Medical sheet: done means back to the card, not closed. */
function EmergencyBack({ person }: { person: Person }) {
  const { goBack } = useResponsiveSheet();
  return <EmergencyPage person={person} onDone={goBack} />;
}

function RelatePage({ subject, onDone }: { subject: Person; onDone: () => void }) {
  const other = getRelateDraft();
  if (!other) return null;
  return <RelationshipPage subject={subject} other={other} onDone={onDone} />;
}

// The header names the card, whatever section or drilled page the sheet opened on.
const PAGE_TITLES: Record<MemberPage, string> = {
  about: 'About',
  contact: 'About',
  family: 'Family',
  relate: 'Family',
  medical: 'Medical',
  emergency: 'Medical',
  relationship: 'How you’re related',
};

function MemberHeader({ person, isMe, page }: { person: Person; isMe: boolean; page: MemberPage }) {
  const { update } = useProfile();
  const setImage = (imageUrl: string | null) =>
    update((d) =>
      isMe
        ? { ...d, me: { ...d.me, imageUrl } }
        : {
            ...d,
            members: d.members.map((m) =>
              m.contactId === person.contactId ? { ...m, imageUrl } : m
            ),
          }
    );
  const sub = isMe ? 'You' : memberSubline(person);
  return (
    <PersonSheetHeader
      photo={<PhotoAvatar person={person} size={72} onChange={setImage} />}
      kicker={PAGE_TITLES[page]}
      name={person.displayName}
      sub={sub}
    />
  );
}

/** Applies a contact patch to the right person in the local data. */
export function usePatchPerson() {
  const { data, api, run, update } = useProfile();
  return (person: Person, patch: ContactPatch, extra?: Partial<Person>) => {
    const isMe = person.contactId === data.me.contactId;
    const apply = (p: Person): Person => {
      const next = { ...p, ...patch, ...extra } as Person;
      next.displayName = `${next.nickname ?? next.firstName} ${next.lastName}`;
      if (patch.householdPositionId !== undefined) {
        next.position =
          data.lookups.householdPositions.find((o) => o.id === patch.householdPositionId)?.label ??
          null;
        next.isChild = patch.householdPositionId === 2 || patch.householdPositionId === 5;
      }
      return next;
    };
    const snapshot = data;
    update((d) =>
      isMe
        ? { ...d, me: { ...d.me, ...apply(d.me) } }
        : { ...d, members: d.members.map((m) => (m.contactId === person.contactId ? apply(m) : m)) }
    );
    return run(
      () => api.updateContact(person.contactId, patch),
      () => update(() => snapshot)
    );
  };
}

/**
 * Everything the About card shows, on one form, the contact fields as the
 * last section. Saving writes locally first; the PUT follows.
 */
export function AboutForm({
  person,
  onDone,
  focusContact = false,
}: {
  person: Person;
  onDone: () => void;
  /** Opened from the card's Contact section: scroll there and start in Email */
  focusContact?: boolean;
}) {
  const { data } = useProfile();
  const { navigate } = useResponsiveSheet();
  const patchPerson = usePatchPerson();
  const { lookups } = data;
  const isMe = person.contactId === data.me.contactId;
  const me = isMe ? data.me : null;
  const [prefixId, setPrefixId] = useState(me?.prefixId ?? null);
  const [first, setFirst] = useState(person.firstName);
  const [middle, setMiddle] = useState(me?.middleName ?? '');
  const [last, setLast] = useState(person.lastName);
  const [suffixId, setSuffixId] = useState(me?.suffixId ?? null);
  const [nickname, setNickname] = useState(person.nickname ?? '');
  const [dob, setDob] = useState(person.dateOfBirth ?? '');
  const [genderId, setGenderId] = useState(person.genderId);
  const [maritalStatusId, setMaritalStatusId] = useState(me?.maritalStatusId ?? null);
  const [positionId, setPositionId] = useState(person.householdPositionId);
  const [shirtSizeId, setShirtSizeId] = useState(person.shirtSizeId);
  const [occupation, setOccupation] = useState(person.occupation ?? '');
  const [email, setEmail] = useState(person.email ?? '');
  const [mobile, setMobile] = useState(person.mobilePhone ?? '');
  const [work, setWork] = useState(me?.workPhone ?? '');

  const valid =
    first.trim().length > 0 &&
    last.trim().length > 0 &&
    (email.trim() === '' || emailOk(email)) &&
    (mobile.trim() === '' || phoneOk(mobile)) &&
    (work.trim() === '' || phoneOk(work));

  const submit = () => {
    const patch: ContactPatch = {
      firstName: first.trim(),
      nickname: nickname.trim() || null,
      lastName: last.trim(),
      dateOfBirth: dob || null,
      genderId,
      shirtSizeId,
      occupation: occupation.trim() || null,
      email: email.trim() || null,
      mobilePhone: mobile.trim() || null,
    };
    if (isMe) {
      patch.workPhone = work.trim() || null;
      patch.prefixId = prefixId;
      patch.middleName = middle.trim() || null;
      patch.suffixId = suffixId;
      patch.maritalStatusId = maritalStatusId;
    } else {
      patch.householdPositionId = positionId;
    }
    void patchPerson(person, patch);
    // Just said "married" with nobody on record: ask who, as the next page
    // of the same sheet, so it feels like one flow.
    if (isMe && isMarried(maritalStatusId) && !findSpouse(data)) navigate('spouse');
    else onDone();
  };

  const shirtGroups = (['Toddler', 'Youth', 'Adult'] as const).map((g) => ({
    name: g,
    options: lookups.shirtSizes.filter((s) => s.group === g).sort((a, b) => a.sort - b.sort),
  }));

  return (
    <Form onSubmit={submit} isValid={valid}>
      {isMe ? (
        <>
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-[8rem_1fr]">
            <SelectField
              label="Prefix"
              value={prefixId}
              onChange={setPrefixId}
              options={lookups.prefixes}
              placeholder="None"
            />
            <TextField
              label="First name"
              value={first}
              onChange={setFirst}
              required
              autoComplete="given-name"
            />
          </div>
          <TextField
            label="Middle name"
            value={middle}
            onChange={setMiddle}
            autoComplete="additional-name"
          />
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-[1fr_8rem]">
            <TextField
              label="Last name"
              value={last}
              onChange={setLast}
              required
              autoComplete="family-name"
            />
            <SelectField
              label="Suffix"
              value={suffixId}
              onChange={setSuffixId}
              options={lookups.suffixes}
              placeholder="None"
            />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
          <TextField
            label="First name"
            value={first}
            onChange={setFirst}
            required
            autoComplete="off"
          />
          <TextField
            label="Last name"
            value={last}
            onChange={setLast}
            required
            autoComplete="off"
          />
        </div>
      )}
      <TextField label="Goes by" value={nickname} onChange={setNickname} placeholder={first} />
      <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
        <TextField
          label="Birthday"
          value={dob}
          onChange={setDob}
          type="date"
          autoComplete={isMe ? 'bday' : 'off'}
        />
        <SelectField
          label="Gender"
          value={genderId}
          onChange={setGenderId}
          options={lookups.genders}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
        {isMe ? (
          <SelectField
            label="Marital status"
            value={maritalStatusId}
            onChange={setMaritalStatusId}
            options={lookups.maritalStatuses}
          />
        ) : (
          <SelectField
            label="In the household"
            value={positionId}
            onChange={setPositionId}
            options={lookups.householdPositions}
          />
        )}
        <GroupedSelectField
          label="Shirt size"
          value={shirtSizeId}
          onChange={setShirtSizeId}
          groups={shirtGroups}
          help="For camp and event shirts."
        />
        <TextField
          label="Occupation"
          value={occupation}
          onChange={setOccupation}
          maxLength={75}
          autoComplete={isMe ? 'organization-title' : 'off'}
          help={
            isMe
              ? "What you do for a living, if you'd like to share it."
              : `What ${person.nickname || person.firstName} does for a living.`
          }
        />
      </div>
      <Focus when={focusContact}>
        <div className="flex flex-col gap-4 border-t border-gray-200 pt-5">
          <Heading size="text-2xl">Contact</Heading>
          <TextField
            label="Email"
            value={email}
            onChange={setEmail}
            type="email"
            inputMode="email"
            autoComplete={isMe ? 'email' : 'off'}
            autoFocus={focusContact}
          />
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <TextField
              label="Mobile phone"
              value={mobile}
              onChange={(v) => setMobile(formatPhone(v))}
              type="tel"
              inputMode="tel"
              autoComplete={isMe ? 'tel' : 'off'}
            />
            {isMe && (
              <TextField
                label="Work phone"
                value={work}
                onChange={(v) => setWork(formatPhone(v))}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
              />
            )}
          </div>
        </div>
      </Focus>
    </Form>
  );
}

/** Shared draft for the spouse flow's search → note pages (module-level: the pages are siblings). */
let spouseDraft: SearchHit | null = null;

/**
 * "Who are you married to?": the adults in the household as one-tap
 * picks (writes both "Married to" rows at once), "Someone else" for a
 * spouse who lives elsewhere (a request they confirm), and Skip.
 */
function SpousePage({ onDone }: { onDone: () => void }) {
  const { data, api, run, update } = useProfile();
  const { navigate } = useResponsiveSheet();
  const rel = marriedToRelationship(data);
  const candidates = data.members.filter((m) => !m.isChild);

  const pick = (p: Person) => {
    if (!rel) return;
    const snapshot = data;
    const tempId = -Date.now();
    update((d) => applyRelation(d, d.me, p, rel, tempId));
    onDone();
    void run(
      async () => {
        if (p.relationship) await api.removeRelationship(p.relationship.contactRelationshipId);
        const saved = await api.addRelationship(p.contactId, rel.id);
        update((d) => replaceRelationId(d, tempId, saved.contactRelationshipId));
      },
      () => update(() => snapshot)
    );
  };

  return (
    <PageBody>
      {candidates.length === 0 && (
        <p className="text-sm text-gray-600">
          Nobody in your household fits yet. Find them and they will confirm.
        </p>
      )}
      {candidates.length > 0 && (
        <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
          {candidates.map((p) => (
            <button
              key={p.contactId}
              type="button"
              onClick={() => pick(p)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
              <Avatar name={p.displayName} imageUrl={p.imageUrl} size={36} />
              <span className="min-w-0 flex-1">
                <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                  {p.displayName}
                </span>
                {p.relationship && (
                  <span className="block truncate text-xs text-gray-500">
                    Now: {p.relationship.label}
                  </span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2.5} />
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('search')}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#299c8f] hover:underline"
        >
          <Search className="h-4 w-4" strokeWidth={2.5} />
          {candidates.length > 0 ? 'Someone else' : 'Find them'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="text-sm font-semibold text-gray-500 hover:underline"
        >
          Skip for now
        </button>
      </div>
    </PageBody>
  );
}

function SpouseSearch() {
  return (
    <SearchPage
      intro="Search for your spouse by name. They get a request to confirm."
      nextPage="note"
      onPick={(p) => {
        spouseDraft = p;
      }}
    />
  );
}

function SpouseNote({ onDone }: { onDone: () => void }) {
  const { data } = useProfile();
  const rel = marriedToRelationship(data);
  if (!spouseDraft || !rel) return null;
  return <NotePage person={spouseDraft} relationship={rel} onClose={onDone} />;
}
